import { useState, useEffect, useRef } from 'react';
import { MagnifyingGlassIcon, UserIcon, CloudIcon } from '@heroicons/react/24/outline';
import { peopleApi } from '../services/api';

interface PersonSearchResult {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  department?: string;
  source: 'azuread' | 'cosmosdb';
  azureAdId?: string;
}

interface PersonSearchProps {
  value?: string;
  onChange: (person: PersonSearchResult | null) => void;
  placeholder?: string;
  disabled?: boolean;
}

export default function PersonSearch({ value, onChange, placeholder = "Search for a person...", disabled }: PersonSearchProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<PersonSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<PersonSearchResult | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load selected person if value is provided
  useEffect(() => {
    if (value && !selectedPerson) {
      // Try to find the person in results or fetch it
      const findPerson = async () => {
        try {
          const searchResults = await peopleApi.search(value);
          const found = searchResults.find(p => p.id === value || p.azureAdId === value);
          if (found) {
            setSelectedPerson(found);
            setSearchTerm(found.name);
          }
        } catch (error) {
          console.error('Failed to load person:', error);
        }
      };
      findPerson();
    }
  }, [value, selectedPerson]);

  // Handle clicks outside the component
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const performSearch = async (query: string) => {
    if (query.length < 2) {
      setResults([]);
      setShowResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const searchResults = await peopleApi.search(query);
      setResults(searchResults);
      setShowResults(true);
    } catch (error) {
      console.error('Search failed:', error);
      setResults([]);
      setShowResults(false);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If cleared, reset selection
    if (!newValue.trim()) {
      setSelectedPerson(null);
      onChange(null);
      setResults([]);
      setShowResults(false);
      return;
    }

    // Debounce search
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(newValue);
    }, 300);
  };

  const handleSelectPerson = (person: PersonSearchResult) => {
    setSelectedPerson(person);
    setSearchTerm(person.name);
    setShowResults(false);
    onChange(person);
  };

  const handleClear = () => {
    setSearchTerm('');
    setSelectedPerson(null);
    setResults([]);
    setShowResults(false);
    onChange(null);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <MagnifyingGlassIcon className="absolute w-5 h-5 transform -translate-y-1/2 left-3 top-1/2 text-secondary-400 dark:text-secondary-500" />
        <input
          type="text"
          value={searchTerm}
          onChange={handleSearchChange}
          onFocus={() => {
            if (results.length > 0) {
              setShowResults(true);
            } else if (searchTerm.length >= 2) {
              performSearch(searchTerm);
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full py-2 pl-10 pr-10 border rounded-lg border-secondary-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-secondary-900 dark:text-secondary-100 placeholder-secondary-400 dark:placeholder-secondary-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-secondary-100 dark:disabled:bg-secondary-800 disabled:cursor-not-allowed"
        />
        {selectedPerson && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600 dark:text-secondary-500 dark:hover:text-secondary-300"
            disabled={disabled}
          >
            ×
          </button>
        )}
      </div>

      {isSearching && (
        <div className="absolute z-10 w-full mt-1 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-600 rounded-lg shadow-lg p-4">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600 dark:border-primary-400"></div>
            <span className="ml-2 text-sm text-secondary-600 dark:text-secondary-400">Searching...</span>
          </div>
        </div>
      )}

      {showResults && results.length > 0 && !isSearching && (
        <div className="absolute z-10 w-full mt-1 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-600 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {results.map((person) => (
            <button
              key={person.id}
              type="button"
              onClick={() => handleSelectPerson(person)}
              className="w-full px-4 py-3 text-left hover:bg-secondary-50 dark:hover:bg-secondary-700 border-b border-secondary-100 dark:border-secondary-700 last:border-b-0 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  <div className={`flex-shrink-0 p-1.5 rounded ${
                    person.source === 'azuread' 
                      ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400' 
                      : 'bg-secondary-100 dark:bg-secondary-700 text-secondary-600 dark:text-secondary-400'
                  }`}>
                    {person.source === 'azuread' ? (
                      <CloudIcon className="w-4 h-4" />
                    ) : (
                      <UserIcon className="w-4 h-4" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <p className="text-sm font-medium text-secondary-900 dark:text-secondary-100 truncate">
                        {person.name}
                      </p>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        person.source === 'azuread'
                          ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300'
                          : 'bg-secondary-100 dark:bg-secondary-700 text-secondary-800 dark:text-secondary-300'
                      }`}>
                        {person.source === 'azuread' ? 'Azure AD' : 'Cosmos DB'}
                      </span>
                    </div>
                    {person.email && (
                      <p className="text-xs text-secondary-600 dark:text-secondary-400 truncate mt-0.5">
                        {person.email}
                      </p>
                    )}
                    {(person.department || person.phone) && (
                      <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-0.5">
                        {person.department && <span>{person.department}</span>}
                        {person.department && person.phone && <span> • </span>}
                        {person.phone && <span>{person.phone}</span>}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {showResults && results.length === 0 && !isSearching && searchTerm.length >= 2 && (
        <div className="absolute z-10 w-full mt-1 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-600 rounded-lg shadow-lg p-4">
          <p className="text-sm text-secondary-500 dark:text-secondary-400 text-center">No people found</p>
        </div>
      )}
    </div>
  );
}
